import Big from "big.js";
import { useCallback, useEffect, useMemo } from "react";
import { DatabaseRow } from "@Shape-Digital/kudzu-data/lib/types/common";

import useDataState from "../../../hooks/useDataState";
import { createSupabaseClient } from "../../../hooks/useSupabase";
import { CenterConfig } from "./types";

export type SupabaseBookingPackageOffering = Pick<
  DatabaseRow<"center_offerings">,
  | "id"
  | "name"
  | "description"
  | "deposit_amount"
  | "price"
  | "scan_side"
  | "duration_minutes"
  | "visibility"
>;

export type SupabaseBookingPackage = Pick<
  DatabaseRow<"center_packages">,
  | "id"
  | "name"
  | "description"
  | "visibility"
  | "center_id"
  | "price"
  | "deposit_amount"
> & {
  center_offerings: SupabaseBookingPackageOffering[];
};

const supabaseBookingPackagesRequest = `
id,
name,
description,
price,
deposit_amount,
visibility,
center_id,
center_offerings (
  id,
  name,
  description,
  deposit_amount,
  price,
  scan_side,
  duration_minutes,
  visibility
)
`;

export type BookingPackage = {
  id: string;
  name: string;
  description: string;
  depositAmount: number;
  price: number;
  durationMinutes: number;
  discountPrice: number;
  addOnIds: string[];
  studyIds: string[];
};

export const parsePackage = (
  packageItem: SupabaseBookingPackage,
  defaultOfferingDurationMinutes: number,
  discountAmount: number,
): BookingPackage => {
  const { id, name, description, center_offerings, price, deposit_amount } =
    packageItem;

  const durationMinutes = center_offerings.reduce(
    (sum, offering) =>
      sum + (offering.duration_minutes ?? defaultOfferingDurationMinutes),
    0,
  );

  const discountPrice = discountAmount
    ? Big(price).minus(discountAmount).toNumber()
    : price;

  return {
    id,
    name,
    description,
    depositAmount: deposit_amount,
    price,
    durationMinutes,
    discountPrice,
    addOnIds: [],
    studyIds: packageItem.center_offerings.map((offering) => offering.id),
  };
};

// includeIds: packages already on the appointment, kept even if archived
// (together with their archived offerings)
const useBookingPackages = ({
  centerConfig,
  includeIds,
}: {
  centerConfig?: CenterConfig;
  includeIds?: string[];
}) => {
  const includeKey = (includeIds ?? []).join(",");

  const supabase = useMemo(() => createSupabaseClient(), []);

  const [dataState, updateDataState] = useDataState<BookingPackage[]>();

  const getPackagesRequest = useCallback(async () => {
    try {
      if (!centerConfig) return;

      const { id, defaultOfferingDurationMinutes, discountAmount } =
        centerConfig;

      updateDataState({ status: "loading" });

      const query = supabase
        .from("center_packages")
        .select(supabaseBookingPackagesRequest)
        .in("center_offerings.visibility" as keyof SupabaseBookingPackage, [
          "public",
          "private",
          "only_package",
          ...(includeKey ? ["archived"] : []),
        ]);

      const response = await (includeKey
        ? query.or(`visibility.in.(public,private),id.in.(${includeKey})`)
        : query.in("visibility", ["public", "private"])
      )
        .eq("center_id", id)
        .returns<SupabaseBookingPackage[]>();

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data) {
        // Archived offerings only count for packages already on the appointment
        const parsedPackages = response.data.map((packageItem) =>
          parsePackage(
            {
              ...packageItem,
              center_offerings: packageItem.center_offerings.filter(
                (offering) =>
                  offering.visibility !== "archived" ||
                  includeKey.split(",").includes(packageItem.id),
              ),
            },
            defaultOfferingDurationMinutes,
            discountAmount,
          ),
        );

        updateDataState({ status: "success", data: parsedPackages });
      }
    } catch (err) {
      updateDataState({ status: "error", error: (err as Error).message });
    }
  }, [centerConfig, supabase, updateDataState, includeKey]);

  useEffect(() => {
    getPackagesRequest();
  }, [getPackagesRequest]);

  return dataState;
};

export default useBookingPackages;
